<!-- EditableGridBase.vue -->
<template>
  <div>
    <!-- Search Bar and Dropdown Filter -->
    <div class="mb-4 flex flex-col md:flex-row md:items-center md:justify-between">
      <input
          type="text"
          v-model="searchQuery"
          @input="debouncedFilterChanged"
          placeholder="Search..."
          class="border border-gray-300 rounded-md px-4 py-2 w-full md:w-1/2 mb-2 md:mb-0"
      />
      <div v-if="dropdownFilterKey" class="flex items-center">
        <label for="dropdownFilter" class="mr-2">Filter by:</label>
        <select
            id="dropdownFilter"
            v-model="dropdownFilter"
            @change="onDropdownFilterChanged"
            class="form-select appearance-auto bg-gray-100 border border-gray-300 rounded-md px-2 py-1 w-96 overflow-ellipsis"
        >
          <option value="">All</option>
          <option
              v-for="key in dropdownOptions"
              :key="key"
              :value="key"
          >
            {{ key }}
          </option>
        </select>
      </div>
      <div v-if="showHospitalsDropdown">
        <div v-if="!areHospitalsLoading" class="flex items-center">
          <label for="hospitalsFilter" class="mr-2">Filter by Hospital:</label>
          <select
              id="hospitalsFilter"
              v-model="hospitalFilter"
              @change="onHospitalsFilterChanged"
              class="form-select appearance-auto bg-gray-100 border border-gray-300 rounded-md px-2 py-1 w-96 overflow-ellipsis"
          >
            <option v-if="!requireHospitalSelection" value="">All</option>
            <option
                v-for="hospital in hospitals"
                :key="hospital.hospital_id"
                :value="hospital.hospital_id"
            >
              {{ hospital.hospital_name }}
            </option>
          </select>
        </div>
        <div v-else class="flex items-center">
          <svg
              class="animate-spin h-8 w-8 text-blue-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
          >
            <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
            ></circle>
            <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            ></path>
          </svg>
        </div>
      </div>
    </div>

    <!-- Undo/Redo Buttons -->
    <!-- Not yet implemented
    <div class="grid-toolbar mb-4">
      <button @click="undoEdit" :disabled="undoStack.length === 0">Undo</button>
      <button @click="redoEdit" :disabled="redoStack.length === 0">Redo</button>
    </div>
    -->

    <ag-grid-vue
        class="ag-theme-balham"
        style="width: 100%; height: 600px;"
        pagination
        :rowData="items"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :gridOptions="computedGridOptions"
        :context="context"
        :components="computedGridComponents"
        :loading="loading || (requireHospitalSelection && hospitalFilter === '')"
        @grid-ready="onGridReady"
        @cell-value-changed="onCellValueChanged"
    ></ag-grid-vue>
  </div>
</template>

<script>
import {AgGridVue} from 'ag-grid-vue3';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';

import {debounce} from "lodash";

import {fetchData, mutate} from "@/services/graphql";
import {useHospitalsStore} from "@/store/hospitals";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";

export default {
  components: {
    AgGridVue,
  },
  data() {
    const hospitalsStore = useHospitalsStore();

    return {
      items: [],
      undoStack: [],
      redoStack: [],
      gridApi: null,
      gridColumnApi: null, // Reference to the grid column API
      searchQuery: '',
      externalSearchQuery: '',
      debouncedFilterChanged: null,
      dropdownFilter: '',
      dropdownOptions: [],
      hospitalFilter: '',
      loading: false,
      hospitalsStore,
      gridOptions: {
        isExternalFilterPresent: () => this.externalSearchQuery.trim() !== '',
        doesExternalFilterPass: (node) => {
          const search = this.externalSearchQuery.trim().toLowerCase();
          if (!search) return true;
          if( this.searchFields.length === 0 ) return true;


          return this.searchFields.some((field) => {
            const value = node.data[field];
            return value && value.toLowerCase().includes(search);
          });
        },
        getRowId: (params) => params.data[this.primaryKey],
        tooltipShowDelay: 1,
        loadingOverlayComponent: 'LoadingOverlay',
      },
      defaultColDef: {
        flex: 1,
        minWidth: 150,
        editable: true,
        resizable: true,
        filter: true,
        sortable: true,
      },
    };
  },
  props: {
    primaryKey: {
      type: String,
      required: true,
    },
    excludedFields: {
      type: Array,
      default: () => [],
    },
    searchFields: {
      type: Array,
      default: () => [],
    },
    updateMutation: {
      type: Object,
      required: true,
    },
    columnDefs: {
      type: Array,
      required: true,
    },
    fetchDataQuery: {
      type: Object,
      required: true,
    },
    components: {
      type: Object,
      default: () => ({}),
    },
    context: {
      type: Object,
      default: () => ({}),
    },
    dropdownFilterKey: {
      type: String,
      default: null,
    },
    showHospitalsDropdown: {
      type: Boolean,
      default: false,
    },
    requireHospitalSelection: {
      type: Boolean,
      default: false,
    }
  },
  created() {
    this.debouncedFilterChanged = debounce(this.onFilterChanged, 300);
    this.fetchData();
  },
  computed: {
    showLoading() {
      return this.loading || (this.requireHospitalSelection && this.hospitalFilter === '');
    },
    computedGridComponents() {
      return {
        LoadingOverlay,
        ...this.components,
      };
    },
    computedGridOptions() {
      return {
        ...this.gridOptions,
        loadingOverlayComponentParams: () => ({isLoading: this.loading}),
      };
    },
    hospitals() {
      if (this.requireHospitalSelection) {
        return [...this.hospitalsStore.hospitals].sort((a, b) => a.hospital_name.localeCompare(b.hospital_name));
      }

      // return only hospitals with a 'hospital_id' that is also found in an item in items
      let hospitalIds = new Set(this.items.map((item) => item.hospital_id));
      return this.hospitalsStore.hospitals.filter((hospital) => hospitalIds.has(hospital.hospital_id))
          .sort((a, b) => a.hospital_name.localeCompare(b.hospital_name));
    },
    areHospitalsLoading() {
      return this.hospitalsStore.isLoading;
    },
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.gridApi?.showLoadingOverlay();
      let queryVariables = {};
      if (this.showHospitalsDropdown && this.requireHospitalSelection) {
        if (this.hospitalFilter === '') {
          this.loading = false;
          return;
        }

        queryVariables = {hospital_id: this.hospitalFilter};
      }
      fetchData(this.fetchDataQuery, queryVariables).then((data) => {
        this.items = data;
        if (this.dropdownFilterKey !== null) {
          this.populateDropdown();
        }
        this.loading = false;
      }).catch((error) => {
        console.error('Error fetching data:', error);
        this.loading = false;
      });
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.$emit('grid-ready', params);
    },
    onFilterChanged() {
      this.externalSearchQuery = this.searchQuery;
      this.gridApi.onFilterChanged();
    },
    populateDropdown() {
      const ids = new Set(this.items.map((item) => item[this.dropdownFilterKey]));
      this.dropdownOptions = Array.from(ids);
    },
    onDropdownFilterChanged() {
      this.applyDropdownFilter();
    },
    applyDropdownFilter() {
      if (this.gridApi) {
        const filterModel = this.gridApi.getFilterModel() || {};
        if (this.dropdownFilterKey !== null) {
          filterModel[this.dropdownFilterKey] = {
            type: 'equals',
            filter: this.dropdownFilter,
          };
        } else {
          delete filterModel[this.dropdownFilterKey];
        }
        this.gridApi.setFilterModel(filterModel);
      }
    },
    onHospitalsFilterChanged() {
      this.applyHospitalsFilter();
      this.$emit('hospital-changed', this.hospitalFilter);
    },
    applyHospitalsFilter() {
      if (this.requireHospitalSelection) {
        this.fetchData();
        return;
      }

      if (this.gridApi) {
        const filterModel = this.gridApi.getFilterModel() || {};
        if (this.hospitalFilter !== '') {
          filterModel['hospital_id'] = {
            type: 'equals',
            filter: this.hospitalFilter,
          };
        } else {
          delete filterModel['hospital_id'];
        }
        this.gridApi.setFilterModel(filterModel);
      }
    },
    onCellValueChanged(params) {
      const updatedItem = params.data;
      const field = params.colDef.field;
      const oldValue = params.oldValue;
      const newValue = params.newValue;

      if (oldValue === newValue || this.excludedFields.includes(field)) {
        return;
      }

      // Add to undo stack
      this.undoStack.push({
        [this.primaryKey]: updatedItem[this.primaryKey],
        field,
        oldValue,
        newValue,
      });

      // Clear redo stack
      this.redoStack = [];

      // Update the server
      this.updateItemOnServer(updatedItem, field, oldValue, params);
    }
    ,
    async updateItemOnServer(item, field, oldValue, params) {
      try {
        const input = {
          [this.primaryKey]: item[this.primaryKey],
          [field]: item[field],
        };

        await mutate(this.updateMutation, {input});
      } catch (error) {
        console.error('Error updating item:', error);

        // Revert the change
        item[field] = oldValue;

        // Refresh the grid
        if (this.gridApi) {
          this.gridApi.refreshCells({
            rowNodes: [params.node],
            columns: [field],
            force: true,
          });
        }

        // Remove from undo stack
        this.undoStack.pop();
      }
    },
    async undoEdit() {
      if (this.undoStack.length === 0) return;

      const lastChange = this.undoStack.pop();
      this.redoStack.push(lastChange);

      await this.applyChange(lastChange, 'oldValue');
    }
    ,
    async redoEdit() {
      if (this.redoStack.length === 0) return;

      const lastUndo = this.redoStack.pop();
      this.undoStack.push(lastUndo);

      await this.applyChange(lastUndo, 'newValue');
    }
    ,
    async applyChange(change, valueKey) {
      const id = change[this.primaryKey];
      const field = change.field;
      const value = change[valueKey];

      const item = this.items.find((i) => i[this.primaryKey] === id);

      if (item) {
        const oldValue = item[field];
        item[field] = value;

        // Refresh the grid
        if (this.gridApi) {
          this.gridApi.refreshCells({
            rowNodes: [this.gridApi.getRowNode(id)],
            columns: [field],
            force: true,
          });
        }

        // Update the server
        await this.updateItemOnServer(item, field, oldValue);
      }
    },
  }
}
;
</script>

<style scoped>
/* Your styles */
</style>
